import React, { PropsWithChildren, useMemo, useRef, useState } from 'react';
import {
  CollapsibleCallback,
  CollapsibleGlobalStateContext,
  CollapsibleRegistration,
} from './CollapsibleGlobalStateContext';
import { CollapsibleExpanderButtonBar } from './CollapsibleExpanderButtonBar';

export function CollapsibleExpander({ showExpander, children }: PropsWithChildren<{ showExpander: boolean }>) {
  const [collapseAllButtonDisabled, setCollapseAllButtonDisabled] = useState(true);
  const [expandAllButtonDisabled, setExpandAllButtonDisabled] = useState(false);
  const subscribers = useRef<Map<string, CollapsibleRegistration>>(new Map<string, CollapsibleRegistration>());
  const providerValueObject = useMemo(
    () => ({
      updateIsOpen: (key: string, state: boolean | ((prev: boolean) => boolean)) => {
        const subscription = subscribers.current.get(key);

        if (!subscription) {
          return;
        }

        if (typeof state === 'function') {
          subscription.state = state(subscription.state);
        } else {
          subscription.state = state;
        }

        subscription.callback(subscription.state);

        setCollapseAllButtonDisabled([...subscribers.current.values()].every(el => !el.state));
        setExpandAllButtonDisabled([...subscribers.current.values()].every(el => el.state));
      },
      subscribe: (key: string, callback: CollapsibleCallback, defaultState: boolean) => {
        subscribers.current.set(key, { state: defaultState, callback });

        return () => subscribers.current.delete(key);
      },
    }),
    [],
  );

  return (
    <>
      {showExpander
        ? (
            <CollapsibleExpanderButtonBar
              collapseAllOnClick={() => {
                [...subscribers.current.keys()].forEach(key => providerValueObject.updateIsOpen(key, false));
              }}
              collapseAllButtonDisabled={collapseAllButtonDisabled}
              expandAllOnClick={() =>
                [...subscribers.current.keys()].forEach(key => providerValueObject.updateIsOpen(key, true))}
              expandAllButtonDisabled={expandAllButtonDisabled}
              ariaControls={[...subscribers.current.keys()].join(' ')}
            />
          )
        : null}
      <CollapsibleGlobalStateContext.Provider value={providerValueObject}>
        {children}
      </CollapsibleGlobalStateContext.Provider>
    </>
  );
}
